import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { QUESTIONS, TYPES, scoreAnswers } from "../data/bookPti";
import { recommendForType } from "../api/recommend";
import { registerBook } from "../api/books";
import { calcSimilarity } from "../utils/similarity";
import captureElement, { FORMATS } from "../utils/captureCard";
import exportCanvas from "../utils/exportImage";
import { track } from "../utils/analytics";
import "./BookPtiModal.css";

/**
 * 북BTI — 어떤 독자인가.
 *
 * 결과를 '유형 이름' 에서 끝내지 않는다. 그 자리에서 내 책, 같은 유형의
 * 사람, 가장 닮은 행성과 둘이 함께 읽은 책까지 이어 붙인다. 유형만 주고
 * 끝나면 한 번 웃고 닫히지만, 행성으로 이어지면 그게 입구가 된다.
 */
const N = ({ children }) => <span className="bp-num">{children}</span>;

/* 결과 카드를 찍는 설정. 카드 안에 이미 서명이 있어 바깥 서명은 끈다 —
   두면 한 장에 두 번 찍힌다. 확인용 화면도 이걸 가져다 써야 실제와 같아진다. */
export const captureOpts = (fmt) => ({ scale: 3, format: fmt, footer: null });

export default function BookPtiModal({ uid, books = [], users = [], myType, onClose, onSave, onBookClick, onUserClick }) {
  const [step, setStep] = useState(myType ? "result" : "intro"); // intro | quiz | result
  const [at, setAt] = useState(0);
  const [answers, setAnswers] = useState({});
  const [typeKey, setTypeKey] = useState(myType || null);
  const [fmt, setFmt] = useState("feed");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [manual, setManual] = useState(null);
  const cardRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /* 질문이 바뀌면 창 위로 올린다. 아래로 내려간 채 다음 질문이 오면
     화면에는 답 버튼만 보여 무엇을 묻는지 사라진다. */
  useEffect(() => { boxRef.current?.scrollTo(0, 0); }, [at, step]);

  const myRead = useMemo(
    () => books.filter((b) => b.completedBy?.[uid]),
    [books, uid]
  );
  const myBookIds = useMemo(() => myRead.map((b) => b.id), [myRead]);

  const type = typeKey ? TYPES[typeKey] : null;
  /* 이 유형이 좋아할 책. 내 기록에서 고르면 이미 읽은 책만 도로 보여주게 되어
     "그래서 뭘 읽지" 로 이어지지 않는다. 카카오에서 찾아와 내 행성에 없는
     책만 고른다. 못 찾으면 이 칸을 통째로 내지 않는다 — 지어내지 않는다. */
  const [recs, setRecs] = useState([]);
  const [adding, setAdding] = useState(null);
  const [added, setAdded] = useState(() => new Set());
  useEffect(() => {
    if (!typeKey) return undefined;
    let alive = true;
    const mineTitles = books.map((b) => b.title);
    recommendForType(typeKey, mineTitles).then((list) => { if (alive) setRecs(list || []); }).catch(() => {});
    return () => { alive = false; };
  }, [typeKey, books]);

  const addToWish = async (b) => {
    if (!uid || adding) return;
    setAdding(b.title);
    try {
      await registerBook(
        { title: b.title, author: b.author, genre: b.genre || null, cover: b.cover || null, description: b.description || null, isbn: b.isbn || null },
        uid,
        { wantToRead: true }
      );
      setAdded((prev) => new Set(prev).add(b.title));
      track("bookpti_wish", { type: typeKey });
    } catch (e) {
      console.error("읽고 싶은 책 담기 실패:", e?.message);
    } finally {
      setAdding(null);
    }
  };
  const sameType = useMemo(
    () => users.filter((u) => u.id !== uid && u.bookType === typeKey),
    [users, uid, typeKey]
  );
  /* 가장 닮은 행성. 이미 프로필에서 쓰는 '취향 유사도' 와 같은 계산이다. */
  const twin = useMemo(() => {
    if (!myBookIds.length) return null;
    const ranked = users
      .filter((u) => u.id !== uid && (u.bookIds || []).length)
      .map((u) => ({ u, pct: calcSimilarity(myBookIds, u.bookIds) }))
      .sort((a, b) => b.pct - a.pct);
    const top = ranked[0];
    if (!top || top.pct <= 0) return null;
    const theirs = top.u.bookIds || [];
    return {
      user: top.u,
      pct: top.pct,
      shared: theirs.filter((id) => myBookIds.includes(id)).length,
      only: theirs.filter((id) => !myBookIds.includes(id)).length,
    };
  }, [users, uid, myBookIds]);

  const answer = (i) => {
    const q = QUESTIONS[at];
    const next = { ...answers, [q.id]: i };
    setAnswers(next);
    if (at + 1 < QUESTIONS.length) { setAt(at + 1); return; }
    const { key } = scoreAnswers(next);
    setTypeKey(key);
    setStep("result");
    track("bookpti_done", { type: key });
    if (onSave) onSave(key);
  };

  const restart = () => { setAnswers({}); setAt(0); setTypeKey(null); setStep("quiz"); };

  const handleExport = async () => {
    if (!cardRef.current || busy) return;
    setBusy(true); setMsg("");
    try {
      const canvas = await captureElement(cardRef.current, captureOpts(fmt));
      const how = await exportCanvas(canvas, `muroo-bookpti-${typeKey}-${fmt}.png`);
      track("bookpti_export", { type: typeKey, format: fmt });
      if (how === "manual") setManual(canvas.toDataURL("image/png"));
      else if (how === "downloaded") setMsg("저장했어요");
    } catch (e) {
      console.error("북BTI 내보내기 실패:", e?.message);
      setMsg("이미지를 만들지 못했어요");
    } finally {
      setBusy(false);
    }
  };

  const q = QUESTIONS[at];

  return createPortal(
    <div className="bp-overlay" onClick={onClose}>
      <div className="bp-box" ref={boxRef} role="dialog" aria-modal="true" aria-label="북BTI"
        onClick={(e) => e.stopPropagation()}>
        <button className="bp-x" onClick={onClose} aria-label="닫기">✕</button>

        {step === "intro" && (
          <div className="bp-intro">
            <div className="bp-kicker">북BTI</div>
            <h2 className="bp-title">당신은 어떤 책의<br />행성에 가까운가요?</h2>
            <p className="bp-lead">
              책을 고르는 방식, 읽는 방식, 읽고 난 뒤 남기는 것들로
              <br />당신의 독서 성향을 발견해보세요.
            </p>
            <div className="bp-art" aria-hidden="true">
              {["🌙", "🔭", "🌱", "⚡", "📜", "🪐", "💫", "🔥"].map((e, i) => (
                <span key={e} style={{ animationDelay: `${i * 0.08}s` }}>{e}</span>
              ))}
            </div>
            <button className="bp-go" onClick={() => { setStep("quiz"); track("bookpti_start"); }}>
              시작하기 · 질문 <N>8</N>개
            </button>
            {myType && (
              <button className="bp-later" onClick={() => setStep("result")}>지난 결과 보기</button>
            )}
          </div>
        )}

        {step === "quiz" && (
          <div className="bp-quiz">
            <div className="bp-progress">
              {QUESTIONS.map((_, i) => (
                <span key={i} className={`bp-dot${i === at ? " on" : ""}${i < at ? " done" : ""}`} />
              ))}
            </div>
            <div className="bp-qn"><N>{at + 1}</N> / <N>{QUESTIONS.length}</N></div>
            <h3 className="bp-q">{q.q}</h3>
            <div className="bp-opts">
              {q.options.map((o, i) => (
                <button key={o.t} className="bp-opt" onClick={() => answer(i)}>{o.t}</button>
              ))}
            </div>
            {at > 0 && <button className="bp-back" onClick={() => setAt(at - 1)}>← 이전 질문</button>}
          </div>
        )}

        {step === "result" && type && (
          <div className="bp-result">
            {/* 이름표로 들어온 사람에게만. 다시 하는 길이 결과 아래에 있다는 걸
                여기서 알린다 — 홈 이름표에 표시를 붙이면 글자가 복잡해진다.
                방금 검사를 마친 사람에게는 필요 없는 말이라 내지 않는다. */}
            {myType && <div className="bp-note">다시 해보고 싶으면 맨 아래 ‘다시 해보기’ 를 누르세요</div>}
            {/* 찍는 건 카드까지. 버튼과 이어지는 길은 뺀다. */}
            <div className="bp-card" ref={cardRef} data-capture-root="1">
              <div className="bp-kicker">나의 북BTI</div>
              <div className="bp-type">
                <span className="bp-type-ic" aria-hidden="true">{type.icon}</span>
                <span className="bp-type-name">{type.name}</span>
              </div>
              <p className="bp-type-lead">{type.lead}</p>
              <p className="bp-type-body">{type.body}</p>

              {recs.length > 0 && (
                <div className="bp-sec">
                  <div className="bp-lbl">이 유형이 좋아할 책</div>
                  <div className="bp-books">
                    {recs.map((b) => (
                      <button key={b.title} className="bp-bookb" onClick={() => addToWish(b)}
                        disabled={adding === b.title || added.has(b.title)}>
                        <img className="bp-cv" src={b.cover} alt={b.title} data-cover="1" />
                        <span className="bp-bt">{b.title}</span>
                        <span className="bp-wish">
                          {added.has(b.title) ? "담았어요" : adding === b.title ? "담는 중…" : "🔖 담기"}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="bp-sec bp-facts">
                <div className="bp-fact">
                  <span className="bp-fact-n"><N>{myRead.length}</N></span>
                  <span className="bp-fact-l">읽은 책</span>
                </div>
                <div className="bp-fact">
                  <span className="bp-fact-n"><N>{sameType.length}</N></span>
                  <span className="bp-fact-l">같은 유형의 행성</span>
                </div>
                {twin && (
                  <div className="bp-fact">
                    <span className="bp-fact-n"><N>{twin.pct}</N>%</span>
                    <span className="bp-fact-l">가장 닮은 행성</span>
                  </div>
                )}
              </div>
              <div className="bp-sign">MUROO · muroo.kr</div>
            </div>

            {twin && (
              <div className="bp-next" data-no-capture="1">
                <div className="bp-lbl">당신과 <N>{twin.pct}</N>% 닮은 행성</div>
                <button className="bp-twin" onClick={() => onUserClick && onUserClick(twin.user.id)}>
                  <span className="bp-twin-av" aria-hidden="true">{twin.user.avatar || "🪐"}</span>
                  <span className="bp-twin-main">
                    <b>{twin.user.name}</b>
                    <small>함께 읽은 책 <N>{twin.shared}</N>권 · 그 행성에만 있는 책 <N>{twin.only}</N>권</small>
                  </span>
                  <span className="bp-twin-go" aria-hidden="true">›</span>
                </button>
              </div>
            )}

            {sameType.length > 0 && (
              <div className="bp-next" data-no-capture="1">
                <div className="bp-lbl">나와 같은 유형</div>
                <div className="bp-same">
                  {sameType.slice(0, 6).map((u) => (
                    <button key={u.id} className="bp-same-u" onClick={() => onUserClick && onUserClick(u.id)}>
                      <span aria-hidden="true">{u.avatar || "🪐"}</span>{u.name}
                    </button>
                  ))}
                  {sameType.length > 6 && <span className="bp-same-more">외 <N>{sameType.length - 6}</N>명</span>}
                </div>
              </div>
            )}

            <div className="bp-fmts" role="group" aria-label="규격">
              {Object.values(FORMATS).map((f) => (
                <button key={f.key} className={`bp-fmt${fmt === f.key ? " on" : ""}`}
                  onClick={() => setFmt(f.key)} aria-pressed={fmt === f.key}>{f.label}</button>
              ))}
            </div>
            <button className="bp-go" onClick={handleExport} disabled={busy}>
              {busy ? "만드는 중…" : "결과를 이미지로 내보내기"}
            </button>
            {msg && <div className="bp-msg">{msg}</div>}
            <button className="bp-later" onClick={restart}>다시 해보기</button>
          </div>
        )}

        {manual && (
          <div className="bp-manual" onClick={() => setManual(null)}>
            <div className="bp-manual-box" onClick={(e) => e.stopPropagation()}>
              <img src={manual} alt="북BTI 결과" />
              <div className="bp-manual-t">그림을 길게 눌러 저장해주세요</div>
              <button className="bp-later" onClick={() => setManual(null)}>닫기</button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
